/**
 * useCheckoutSuccess — Stripe-return detection + UX follow-up.
 *
 * After a successful checkout Stripe redirects back to the app with
 * `?checkout=success` in the URL. This hook owns:
 *  - detecting that flag once on mount
 *  - clearing it from the URL (no re-entry on refresh)
 *  - firing a `user-invalidate` bus event so UserProvider refetches
 *    the new plan/access snapshot
 *  - exposing an `open` boolean + `close()` for a success modal
 *
 * Extracted from App.tsx to keep the shell thin.
 */

import { useEffect, useState } from 'react';
import { emit as emitAppEvent } from '../events/bus';

/** Read the `?checkout=success` flag synchronously at component init.
 *  SSR-safe: returns false when `window` isn't defined. */
function detectCheckoutSuccess(): boolean {
    if (typeof window === 'undefined') return false;
    const params = new URLSearchParams(window.location.search);
    return params.get('checkout') === 'success';
}

export function useCheckoutSuccess(): { open: boolean; close: () => void } {
    // Initial state derived from the URL — keeps the effect free of
    // setState (which the react-hooks/effects rule flags as a
    // potential cascade).
    const [open, setOpen] = useState<boolean>(detectCheckoutSuccess);

    useEffect(() => {
        if (!open) return;
        // Side effects only: clear the URL flag so refresh doesn't
        // reopen the modal, then ask UserProvider to refetch so the
        // new plan + access snapshot lands.
        window.history.replaceState({}, '', window.location.pathname);
        emitAppEvent('user-invalidate', { reason: 'checkout' });
    }, [open]);

    return { open, close: () => setOpen(false) };
}
